// Spec §4.3: Extract debounce click logic from ToggleButton component
import dayjs from 'dayjs';
import { useRef } from 'react';
import type { ToggleButtonProps } from './types';

/**
 * Debounces a toggle button press before reporting the selected value upward. Defaults isDebounceClick to true and the name to an empty string, exposing an onClick that drops presses within 300ms of the previous one before calling props.onClick with the name and props.data.value.
 *
 * @param props - The toggle button props, carrying the parent onClick callback, the button data and the isDebounceClick flag.
 * @returns The debounced toggle click handler.
 */
export const useToggleButton = (props: ToggleButtonProps) => {
  const { isDebounceClick = true, name = '' } = props;
  const debounceClickBottomButton = useRef(0);

  /**
   * Reports the toggle button's name and data value to the parent while suppressing repeat presses. Returns early when debouncing is enabled and less than 300ms elapsed since the ref timestamp, otherwise records the time and calls props.onClick with the name and props.data.value.
   *
   * @returns Nothing.
   */
  const onClick = () => {
    if (isDebounceClick && dayjs().valueOf() - debounceClickBottomButton.current < 300) {
      return;
    }

    debounceClickBottomButton.current = dayjs().valueOf();
    props.onClick(name, props.data.value);
  };

  return { onClick };
};
